// Eszköz-cellák — a lista (`routes/assets.tsx`) és az igény-sáv olvasó-nézete (`asset-request-details.tsx`) KÖZÖS
// megjelenítése: a típus a kategória-jelvénnyel, és az ár nettó / áfa / bruttó bontásban. Egy forrás, hogy a két hely
// ne csússzon el egymástól.
import { useTranslation } from 'react-i18next';

import { AssetCategoryBadge } from '@/components/assets/asset-category-badge';
import { Divider } from '@/components/ui/divider';
import { type AssetCatalogLookup } from '@/features/assets/catalog';
import { fmtHuf, fmtPercent, priceParts } from '@/features/assets/price';
import { cn } from '@/lib/utils';

/** Az ár nettó / áfa / bruttó bontásban, egymás alatt (a bruttó előtt hajszálvonal). Érvénytelen ár → `null`. */
export function AssetPriceBreakdown({
  netPrice,
  vatRate,
  className,
}: {
  netPrice: string | null;
  vatRate: string | null;
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const parts = priceParts(netPrice, vatRate);
  if (!parts) return null;
  return (
    <div className={cn('flex min-w-0 max-w-[13rem] flex-col gap-0.5 text-xs', className)}>
      <div className="flex justify-between gap-3">
        <span className="text-muted-foreground">{t('assets.priceNet')}</span>
        <span className="text-foreground font-medium tabular-nums">{fmtHuf(parts.net)}</span>
      </div>
      <div className="flex justify-between gap-3">
        <span className="text-muted-foreground">{t('assets.priceVat')}</span>
        <span className="tabular-nums">{fmtPercent(parts.vatPct)}</span>
      </div>
      <Divider tone="hairline" inset={false} spacing="none" className="my-0.5" />
      <div className="flex justify-between gap-3">
        <span className="text-muted-foreground">{t('assets.priceGross')}</span>
        <span className="tabular-nums">{fmtHuf(parts.gross)}</span>
      </div>
    </div>
  );
}

/** A típus neve, alatta a kategória-jelvény (hardver / szoftver — a katalógus nevével). */
export function AssetTypeWithCategory({
  type,
  category,
  lookup,
}: {
  type: string;
  category: string;
  lookup: AssetCatalogLookup;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <div className="max-w-full truncate">{lookup.typeName(type, t)}</div>
      <AssetCategoryBadge
        category={lookup.isSoftwareType(type) ? 'software' : 'hardware'}
        label={lookup.categoryName(category, t)}
        className="max-w-full truncate"
      />
    </div>
  );
}
